import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Table, Button, Space, Tag, message, Popconfirm, Select, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CreateFederationModal } from '../CreateFederationModal';
import { EditFederationModal } from '../EditFederationModal';
import apiService from '@/services/api.service';
import type { Federation as FederationModel, FederationWithItems } from '@/types/models/club';
import type { ApiResponseData } from '@/types/api';
import type { Federation } from '@/services/federation.service';
import { logger } from '@/utils/logger';
import { API_ENDPOINTS } from '@/constants/api.constants';

const FederationsPage: React.FC = () => {
    const location = useLocation();
    const [federations, setFederations] = useState<FederationModel[]>([]);
    const [filteredFederations, setFilteredFederations] = useState<FederationModel[]>([]);
    const [federationStatusFilter, setFederationStatusFilter] = useState<string>('all');
    const [isLoading, setIsLoading] = useState(false);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [isEditFederationModalVisible, setIsEditFederationModalVisible] = useState(false);
    const [selectedFederation, setSelectedFederation] = useState<FederationModel | null>(null);

    const fetchFederations = async () => {
        try {
            setIsLoading(true);
            const response = await apiService.get<ApiResponseData<Federation>>(
                API_ENDPOINTS.FEDERATIONS.BASE,
                {
                    params: {
                        page: 1,
                        limit: 100,
                    }
                }
            );

            if (response.data?.data) {
                const items = Array.isArray(response.data.data)
                    ? response.data.data as FederationModel[]
                    : (response.data.data as FederationWithItems).items
                        ? (response.data.data as FederationWithItems).items as FederationModel[]
                        : [response.data.data] as FederationModel[];

                setFederations(items);
                filterFederationsByStatus(items, federationStatusFilter);
            } else {
                setFederations([]);
                setFilteredFederations([]);
            }
        } catch (error) {
            logger.error('Error fetching federations:', error);
            message.error('Ошибка при загрузке федераций');
            setFederations([]);
            setFilteredFederations([]);
        } finally {
            setIsLoading(false);
        }
    };

    const filterFederationsByStatus = (feds: FederationModel[], status: string) => {
        if (status === 'all') {
            setFilteredFederations(feds);
        } else {
            setFilteredFederations(feds.filter(f => f.status === status));
        }
    };

    const handleFederationStatusFilterChange = (value: string) => {
        setFederationStatusFilter(value);
        filterFederationsByStatus(federations, value);
    };

    const handleCreateSuccess = () => {
        setIsCreateModalVisible(false);
        fetchFederations();
    };

    const handleEditFederation = (federation: FederationModel) => {
        setSelectedFederation(federation);
        setIsEditFederationModalVisible(true);
    };

    const handleDeleteFederation = async (id: number) => {
        try {
            await apiService.delete(API_ENDPOINTS.FEDERATIONS.DELETE(id));
            message.success('Федерация успешно удалена');
            fetchFederations();
        } catch (error) {
            logger.error('Error deleting federation:', error);
            message.error('Ошибка при удалении федерации');
        }
    };

    useEffect(() => {
        if (location.pathname === '/admin/federations') {
            fetchFederations();
        }
    }, [location.pathname]);

    useEffect(() => {
        filterFederationsByStatus(federations, federationStatusFilter);
    }, [federations, federationStatusFilter]);

    const federationColumns: ColumnsType<FederationModel> = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                        placeholder="Поиск по названию"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Поиск
                        </Button>
                        <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) =>
                record.name.toLowerCase().includes(String(value).toLowerCase()),
        },
        {
            title: 'Регион',
            dataIndex: 'region',
            key: 'region',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                        placeholder="Поиск по региону"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Поиск
                        </Button>
                        <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) =>
                record.region?.toLowerCase().includes(String(value).toLowerCase()) ?? false,
        },
        {
            title: 'Президент',
            dataIndex: 'president',
            key: 'president',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                        placeholder="Поиск по президенту"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Поиск
                        </Button>
                        <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) =>
                record.president?.toLowerCase().includes(String(value).toLowerCase()) ?? false,
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            filters: [
                { text: 'Активна', value: 'active' },
                { text: 'Неактивна', value: 'inactive' },
            ],
            onFilter: (value, record) => record.status === value,
            render: (status: string) => (
                <Tag color={status === 'active' ? 'green' : 'red'}>
                    {status === 'active' ? 'Активна' : 'Неактивна'}
                </Tag>
            ),
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: any, federation: FederationModel) => (
                <Space>
                    <Button
                        type="link"
                        onClick={() => handleEditFederation(federation)}
                    >
                        Редактировать
                    </Button>
                    <Popconfirm
                        title="Удалить федерацию?"
                        description="Это действие нельзя отменить"
                        onConfirm={() => handleDeleteFederation(federation.id)}
                        okText="Да"
                        cancelText="Нет"
                    >
                        <Button type="link" danger>
                            Удалить
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                    type="primary"
                    onClick={() => setIsCreateModalVisible(true)}
                >
                    Добавить федерацию
                </Button>
                <div>
                    <span style={{ marginRight: 8 }}>Статус:</span>
                    <Select
                        value={federationStatusFilter}
                        style={{ width: 120 }}
                        onChange={handleFederationStatusFilterChange}
                        options={[
                            { value: 'all', label: 'Все' },
                            { value: 'active', label: 'Активные' },
                            { value: 'inactive', label: 'Неактивные' },
                        ]}
                    />
                </div>
            </div>
            <Table
                columns={federationColumns}
                dataSource={filteredFederations as any[]}
                rowKey="id"
                loading={isLoading}
            />
            <CreateFederationModal
                visible={isCreateModalVisible}
                onCancel={() => setIsCreateModalVisible(false)}
                onSuccess={handleCreateSuccess}
            />
            <EditFederationModal
                visible={isEditFederationModalVisible}
                federation={selectedFederation as any}
                onCancel={() => {
                    setIsEditFederationModalVisible(false);
                    setSelectedFederation(null);
                }}
                onSuccess={() => {
                    setIsEditFederationModalVisible(false);
                    setSelectedFederation(null);
                    fetchFederations();
                    message.success('Федерация успешно обновлена');
                }}
            />
        </div>
    );
};

export default FederationsPage;


